import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BandCard from "../../components/BandCard";
import Button from "../../components/Button";
import IncidentIcon from "/public/images/icons/incidentIconWhite.svg?react";
import { format } from "date-fns";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { useDrawerContext } from "../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function EmergencyScan() {
    const { user, selectedZone, selectedVenue, logout, updateTokens } = useUserContext();
    const { setTitle, setBackButton, setBackButtonFunc } = useHeaderContext();
    const { setOpen, setDrawerContent } = useDrawerContext();
    const { state } = useLocation();
    const { band } = state;
    const [bandProfile, setBandProfile] = useState(null);
    const navigate = useNavigate();

    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);

    useEffect(() => {
        setTitle("SCANNED BAND");
        setBackButton(true);
        setBackButtonFunc(() => navigate('/scan?type=emergency'));

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(success, error);
        } else {
            LogErrors(user, logout, updateTokens, "Geolocation not supported");
        }
        
        function success(position) {
            setLatitude(position.coords.latitude);
            setLongitude(position.coords.longitude);
        }
        
        function error() {
            LogErrors(user, logout, updateTokens, "Unable to retrieve your location");
        }

        const getProfile = async () => {
            await Sentry.startSpan({ name: "Fetch User Profile", op: "http.request" }, async (span) => {
              try {
                const url = `${import.meta.env.VITE_API_URL}Profile/FetchUserProfile?participantid=${band.ParticipantId}`;
                span.setAttribute("http.url", url);
                span.setAttribute("ParticipantId", band.ParticipantId);
          
                const response = await fetchWithRefresh(
                  url,
                  {
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${user.token}`,
                    },
                  }, logout, updateTokens, (message) => { toast.info(message);}
                );
          
                if (response.ok) {
                  const data = await response.json();
                  setBandProfile(data);
                  span.setStatus("ok");
                } else {
                  const errorText = await response.text();
                  LogErrors(user, logout, updateTokens, "Error fetching user profile: " + errorText);
                  span.setStatus("error");
                  throw new Error(`Failed to fetch user profile: ${errorText}`);
                }
              } catch (error) {
                Sentry.captureException(error, { extra: { ParticipantId: band.ParticipantId } });
                LogErrors(user, logout, updateTokens, "Couldn't reach user profile endpoint: " + error);
                span.setStatus("error");
              } finally {
                span.end();
              }
            });
        };

        getProfile();
    }, []);

    const declareEmergency = async () => {
        await Sentry.startSpan({ name: "Declare Emergency", op: "http.request" }, async (span) => {
          try {
            const url = `${import.meta.env.VITE_API_URL}Incident/DeclareIncident`;
            span.setAttribute("http.url", url);
            span.setAttribute("BandID", band.BandID);
            span.setAttribute("UserID", user.id);
            span.setAttribute("Latitude", latitude);
            span.setAttribute("Longitude", longitude);
            span.setAttribute("VenueID", selectedVenue.OrgID);
            span.setAttribute("ZoneID", selectedZone.ZoneID);
      
            const response = await fetchWithRefresh(
              url,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${user?.token}`,
                },
                body: JSON.stringify({
                  scanId: band.BandID,
                  trigerringUserId: user.id,
                  lat: `${latitude}`, // Ensure latitude is a string
                  lng: `${longitude}`, // Ensure longitude is a string
                  venueId: selectedVenue.OrgID,
                  venueZone: selectedZone.ZoneID,
                }),
              }, logout, updateTokens, (message) => { toast.info(message);}
            );
      
            if (response.ok) {
              const data = await response.json();
              span.setStatus("ok");
              navigate(`/incidents/${data.IncidentId}`);
            } else {
              const errorText = await response.text();
              span.setStatus("error");
              throw new Error(`Failed to declare emergency: ${errorText}`);
            }
          } catch (error) {
            Sentry.captureException(error, { extra: { BandID: band.BandID, UserID: user.id } });
            LogErrors(user, logout, updateTokens, "Couldn't reach check-in endpoint: " + error);
      
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    ERROR
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                  <h3 className="text-[24px]">Error generating incident</h3>
                  <p className=" text-sportsDarkGrey">{error.message}</p>
                  <Button variant="primary" onClick={() => setOpen(false)}>Close</Button>
                </div>
              </div>
            );
          } finally {
            span.end();
          }
        });
    };

    return (
        <div className="flex flex-col gap-[30px] bg-[#F8F8F8] p-[24px] pb-20 min-h-full">
            <div className="w-full flex flex-col gap-[24px] items-center text-center">
            <img alt="Emergency Scan" src="/images/icons/emergencyBandScan.svg" width={50} height={50} />
            <div className="font-poppins font-semibold text-[24px]">
                Band Scanned
            </div>
            </div>
            <BandCard band={band} size="sm" participant={band} />

            <div className="mt-5">
                <h3>{band?.ParticipantName}</h3>
                <p className="text-sportsGrey capitalize">{band?.BandStateVerbose.toLowerCase()} • Last Updated • {format(new Date(band?.LastUpdated), 'dd/MM/yyyy')}
                </p>
            </div>

            <div className="dashed-border w-full h-0.5" />

            <div className="flex flex-col gap-8">
            <div className='flex flex-col gap-y-1'>
            <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Name (ICE)</p>
            <p className='font-poppins text-[#727272]'>{bandProfile?.PrimaryIceName}</p>
          </div>
          <div className='flex flex-col gap-y-1'>
            <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Number</p>
            <p className='font-poppins text-[#727272]'>{bandProfile?.PrimaryIceNumber}</p>
          </div>
          <div className='flex flex-col gap-y-1'>
            <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Relationship</p>
            <p className='font-poppins text-[#727272]'>{bandProfile?.PrimaryIceRelationship}</p>
          </div>
          <div className='flex flex-col gap-y-1'>
            <p className='font-poppins font-semibold text-[16px]'>Primary Medical Condition</p>
            <p className='font-poppins text-[#727272]'>{bandProfile?.PrimaryMedicalCondition}</p>
          </div>
          <div className='flex flex-col gap-y-1'>
            <p className='font-poppins font-semibold text-[16px]'>Notes</p>
            <p className='font-poppins text-[#727272]'>{bandProfile?.Notes}</p>
          </div>

          <div className="dashed-border w-full h-0.5" />

          <div className="flex">
          <div className='flex flex-col gap-y-1 w-1/2'>
            <p className='font-poppins font-semibold text-[16px]'>Venue</p>
            <p className='font-poppins text-[#727272] capitalize'>{selectedVenue?.OrgPartnerFriendlyName}</p>
          </div>
          <div className='flex flex-col gap-y-1 w-1/2'>
            <p className='font-poppins font-semibold text-[16px]'>Zone</p>
            <p className='font-poppins text-[#727272] capitalize'>{band?.CurrentZoneName || selectedZone?.ZoneName}</p>
          </div>
          </div>
       {latitude && longitude && (
           <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
           <Map
             style={{width: '100%', height: '174px', borderRadius: '4px', border: '1px solid #E3E3E3'}}
             center={{
               lat: parseFloat(latitude), 
               lng: parseFloat(longitude)
             }}
             defaultCenter={{
               lat: parseFloat(latitude), 
               lng: parseFloat(longitude)
             }}
             defaultZoom={16}
             gestureHandling={'greedy'}
             disableDefaultUI={true}
           />
         </APIProvider>
        )}
            </div>
            <div className="flex flex-col gap-6">
                <Button status="emergency" onClick={declareEmergency}><IncidentIcon />Generate Emergency Incident</Button>
                <Button variant="quaternary" className="w-full" onClick={() => navigate('/scan?type=emergency')}>No, Everything Is Fine</Button>
            </div>
        </div>
    )
}